// AdminUsersLayout — správa uživatelů (jen pro is_admin = 'Y').
// Tabulka uživatelů, vytváření, editace, mazání, schvalování / blokace, reset hesla.
// Rozbalovací sekce práv pod každým uživatelem — dvě nezávislé osy:
//   access_source ('open'|'oracle')          — který katalog dat uživatel smí číst
//   online_source ('none'|'open'|'oracle')   — zda a odkud smí server online stahovat
//
// Stav účtu (status): approved | pending | blocked. Přihlásit se smí jen approved.
// Admina nelze smazat ani zablokovat — nejdřív jiný admin odebere práva. Účet 'admin'
// nelze smazat nikdy. Server (cw-server ČÁST B) tyto pravidla vynucuje, klient jen zrcadlí.
import { useState, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../api/adminApi'
import { useAuthStore } from '../store/useAuthStore'
import { ModalOverlay } from '../components/ModalOverlay'
import { useTrackView } from '../hooks/useTrackProduct'

// ─── Pomocné komponenty ────────────────────────────────────────────────────────

function Badge({ children, className = 'bg-gray-100 text-gray-500' }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {children}
    </span>
  )
}

function formatDate(val) {
  if (!val) return '—'
  return new Date(val).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })
}

function formatDateOnly(val) {
  if (!val) return null
  return new Date(val).toLocaleDateString('cs-CZ', { dateStyle: 'short' })
}

const STATUS_META = {
  approved: { label: 'Schválený',        cls: 'bg-green-100 text-green-700' },
  pending:  { label: 'Čeká na schválení', cls: 'bg-amber-100 text-amber-700' },
  blocked:  { label: 'Blokovaný',         cls: 'bg-red-100 text-red-700' },
}

const ACCESS_LABELS = { open: 'Open data', oracle: 'Oracle' }
const ONLINE_LABELS = { none: 'Vypnuto', open: 'Open (CHMI)', oracle: 'Oracle' }

// ─── Inline badges práv v řádku tabulky ──────────────────────────────────────

function UserAccessBadges({ userId }) {
  const { data: access, isLoading } = useQuery({
    queryKey: ['user-access', userId],
    queryFn:  () => adminApi.getUserAccess(userId),
  })

  if (isLoading) return <span className="text-xs text-gray-300">…</span>
  if (!access)   return <span className="text-xs text-gray-300">—</span>

  return (
    <div className="flex flex-wrap gap-1">
      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
        title="Přístup k datům (který katalog uživatel čte)">
        {ACCESS_LABELS[access.access_source] || access.access_source}
      </span>
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
        access.online_source === 'none' ? 'bg-gray-100 text-gray-500' : 'bg-emerald-100 text-emerald-700'}`}
        title="Online stahování (odkud server dotahuje čerstvá data)">
        ↓ {ONLINE_LABELS[access.online_source] || access.online_source}
      </span>
    </div>
  )
}

// ─── Rozbalovací sekce práv ──────────────────────────────────────────────────

function UserAccessRow({ userId }) {
  const qc = useQueryClient()

  const { data: access, isLoading } = useQuery({
    queryKey: ['user-access', userId],
    queryFn:  () => adminApi.getUserAccess(userId),
  })

  const mutation = useMutation({
    mutationFn: (patch) => adminApi.setUserAccess(userId, patch),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['user-access', userId] }),
  })

  const selectCls = 'text-xs border border-gray-300 rounded-lg px-2 py-1 text-gray-700 bg-white ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer disabled:opacity-50'

  return (
    <tr className="bg-blue-50/40">
      <td colSpan={9} className="px-6 py-3 border-b border-blue-100">
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Práva uživatele</p>

          {isLoading && <p className="text-xs text-gray-400">Načítám…</p>}

          {!isLoading && access && (
            <div className="flex flex-wrap items-end gap-6">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">
                  Přístup k datům
                  <span className="text-gray-400"> — který katalog uživatel čte</span>
                </span>
                <select
                  value={access.access_source}
                  onChange={e => mutation.mutate({ access_source: e.target.value })}
                  disabled={mutation.isPending}
                  className={selectCls}
                >
                  {Object.entries(ACCESS_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">
                  Online stahování
                  <span className="text-gray-400"> — odkud server dotahuje čerstvá data</span>
                </span>
                <select
                  value={access.online_source}
                  onChange={e => mutation.mutate({ online_source: e.target.value })}
                  disabled={mutation.isPending}
                  className={selectCls}
                >
                  {Object.entries(ONLINE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>

              {mutation.isError && (
                <span className="text-xs text-red-500 pb-1">{mutation.error?.message}</span>
              )}
              {access.updated_by_username && (
                <span className="text-xs text-gray-400 pb-1">naposledy upravil {access.updated_by_username}</span>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Dialog s odkazem pro nastavení hesla ────────────────────────────────────
// Sdílený pro odpovědi createUser / setUserStatus / resetUserPassword nesoucí reset_link.

function ResetLinkDialog({ result, onClose }) {
  const [copied, setCopied] = useState(false)
  const link = result.reset_link

  function copy() {
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <ModalOverlay onClose={onClose} className="z-[60]">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg flex flex-col gap-4">
        <h3 className="text-lg font-bold text-gray-800">Odkaz pro nastavení hesla</h3>
        <p className="text-sm text-gray-600">
          {result.message}
          {result.expires_at && (
            <> Platný do <strong>{formatDate(result.expires_at)}</strong>.</>
          )}
        </p>
        <input
          readOnly
          value={link}
          onFocus={e => e.target.select()}
          className="border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono text-gray-700 bg-gray-50"
        />
        <div className="flex gap-3 justify-end">
          <button onClick={copy}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
            {copied ? 'Zkopírováno' : 'Kopírovat'}
          </button>
          <button onClick={() => window.open(link, '_blank', 'noopener')}
            className="px-4 py-2 text-sm rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors">
            Otevřít a nastavit heslo
          </button>
          <button onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">
            Zavřít
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ─── Informační dialog (výsledek akce bez odkazu, např. změna username) ─────

function MessageDialog({ title, message, onClose }) {
  return (
    <ModalOverlay onClose={onClose} className="z-[60]">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4">
        <h3 className="text-lg font-bold text-gray-800">{title}</h3>
        <p className="text-sm text-gray-600">{message}</p>
        <div className="flex justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">
            Zavřít
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ─── Dialog schválení (s nastavením práv) ────────────────────────────────────

function ApproveDialog({ user, onClose, onApproved }) {
  const [access, setAccess] = useState('open')
  const [online, setOnline] = useState('none')
  const [error, setError]   = useState(null)

  const mutation = useMutation({
    mutationFn: () => adminApi.setUserStatus(user.id, {
      status: 'approved', access_source: access, online_source: online,
    }),
    onSuccess: (res) => onApproved(res),
    onError:   (err) => setError(err.message),
  })

  const selectCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'

  return (
    <ModalOverlay onClose={onClose} className="z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4">
        <h3 className="text-lg font-bold text-gray-800">Schválit účet: {user.username}</h3>

        {user.admin_note && (
          <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 whitespace-pre-wrap">
            {user.admin_note}
          </div>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-600 font-medium">Přístup k datům</span>
          <select value={access} onChange={e => setAccess(e.target.value)} className={selectCls}>
            {Object.entries(ACCESS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-600 font-medium">Online stahování</span>
          <select value={online} onChange={e => setOnline(e.target.value)} className={selectCls}>
            {Object.entries(ONLINE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>

        <p className="text-xs text-gray-400">
          Uživateli se odešle e-mail s odkazem pro nastavení hesla (pokud má e-mail).
        </p>

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

        <div className="flex gap-3 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
            Zrušit
          </button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
            className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors">
            {mutation.isPending ? 'Schvaluji…' : 'Schválit'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ─── Dialog zamítnutí registrace (= smazání) ─────────────────────────────────

function RejectDialog({ user, onClose, onDone }) {
  const [notify, setNotify] = useState(!!user.email)
  const [error, setError]   = useState(null)

  const mutation = useMutation({
    mutationFn: () => adminApi.deleteUser(user.id, { notify: notify && !!user.email }),
    onSuccess:  () => onDone(),
    onError:    (err) => setError(err.message),
  })

  return (
    <ModalOverlay onClose={onClose} className="z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
        <h3 className="text-lg font-bold text-gray-800">Zamítnout registraci?</h3>
        <p className="text-sm text-gray-600">
          Registrace <strong>{user.username}</strong> bude <strong>smazána</strong>.
          Uživatelské jméno i e-mail se uvolní.
        </p>
        {user.email && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} className="accent-blue-600" />
            Poslat uživateli e-mail o zamítnutí
          </label>
        )}
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex gap-3 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
            Zrušit
          </button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">
            {mutation.isPending ? 'Mažu…' : 'Zamítnout a smazat'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ─── Dialog smazání uživatele (opsání věty) ──────────────────────────────────

function DeleteUserDialog({ user, onClose, onDone }) {
  const [confirm, setConfirm] = useState('')
  const [error, setError]     = useState(null)
  const phrase = `smazat ${user.username}`

  const mutation = useMutation({
    mutationFn: () => adminApi.deleteUser(user.id),
    onSuccess:  () => onDone(),
    onError:    (err) => setError(err.message),
  })

  return (
    <ModalOverlay onClose={onClose} className="z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
        <h3 className="text-lg font-bold text-gray-800">Smazat uživatele?</h3>
        <p className="text-sm text-gray-600">
          Trvale smazat uživatele <strong>{user.username}</strong>? Tato akce je nevratná.
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Pro potvrzení opište: <code className="text-gray-700">{phrase}</code></span>
          <input
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
          />
        </label>
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex gap-3 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
            Zrušit
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || confirm !== phrase}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending ? 'Mažu…' : 'Smazat'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ─── Dialog reset hesla ─────────────────────────────────────────────────────

function ResetPasswordDialog({ user, onClose, onDone }) {
  const [error, setError] = useState(null)

  const mutation = useMutation({
    mutationFn: ({ sendEmail }) => adminApi.resetUserPassword(user.id, { sendEmail }),
    onSuccess:  (res) => onDone(res),
    onError:    (err) => setError(err.message),
  })

  return (
    <ModalOverlay onClose={onClose} className="z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
        <h3 className="text-lg font-bold text-gray-800">Reset hesla: {user.username}</h3>
        <p className="text-sm text-gray-600">
          Stávající heslo bude zneplatněno a vygeneruje se nový odkaz pro nastavení hesla.
        </p>
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex flex-col gap-2">
          {user.email && (
            <button
              onClick={() => mutation.mutate({ sendEmail: true })}
              disabled={mutation.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              Reset + poslat e-mail uživateli
            </button>
          )}
          <button
            onClick={() => mutation.mutate({ sendEmail: false })}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Reset bez e-mailu (odkaz předám sám)
          </button>
          <button onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-gray-500 hover:bg-gray-50 transition-colors">
            Zrušit
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ─── Modální dialog uživatele (vytvoření / editace) ──────────────────────────

function UserModal({ user, currentUserId, onClose, onResult }) {
  const isNew = !user
  const qc    = useQueryClient()

  const [form, setForm] = useState({
    username:     user?.username     ?? '',
    password:     '',
    first_name:   user?.first_name   ?? '',
    last_name:    user?.last_name    ?? '',
    display_name: user?.display_name ?? '',
    email:        user?.email        ?? '',
    is_admin:     user?.is_admin     ?? 'N',
    admin_note:   user?.admin_note   ?? '',
    valid_from:   user?.valid_from   ? String(user.valid_from).slice(0, 10) : '',
    valid_to:     user?.valid_to     ? String(user.valid_to).slice(0, 10) : '',
  })
  const [error, setError] = useState(null)

  const mutation = useMutation({
    mutationFn: (data) => isNew ? adminApi.createUser(data) : adminApi.updateUser(user.id, data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      // Admin přejmenoval sám sebe → srovnat username v auth storu (práva beze změny).
      if (res?.username_changed && user?.id === currentUserId)
        useAuthStore.setState(s => ({ user: s.user && { ...s.user, username: res.username } }))
      // createUser s prázdným heslem vrací reset_link → předat rodiči na ResetLinkDialog;
      // změna username vrací message (výsledek e-mailu) → předat rodiči na MessageDialog.
      if (res?.reset_link || res?.username_changed) onResult(res)
      onClose()
    },
    onError: (err) => setError(err.message),
  })

  function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    const data = { ...form, username: form.username.trim() }
    if (!isNew && !data.password) delete data.password
    // Beze změny username neposílat — server by jinak zbytečně validoval.
    if (!isNew && data.username === user.username) delete data.username
    // prázdné datum → null (server očekává YYYY-MM-DD nebo null)
    data.valid_from = data.valid_from || null
    data.valid_to   = data.valid_to   || null
    mutation.mutate(data)
  }

  function field(label, key, type = 'text', opts = {}) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-xs text-gray-500 font-medium">{label}</label>
        <input
          type={type}
          value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          {...opts}
        />
      </div>
    )
  }

  const isSelf     = user?.id === currentUserId
  const isAdminAcc = user?.username === 'admin'
  const usernameChanged = !isNew && form.username.trim() !== '' && form.username.trim() !== user.username
  // Heslo: povinné jen když je nový účet BEZ e-mailu.
  const passwordRequired = isNew && !form.email

  return (
    <ModalOverlay onClose={onClose} className="z-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md flex flex-col gap-5 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-800">{isNew ? 'Nový uživatel' : `Editace: ${user.username}`}</h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {field('Uživatelské jméno *', 'username', 'text', {
            required: true, autoComplete: 'off', disabled: isAdminAcc,
            className: 'border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:text-gray-500',
          })}
          {isAdminAcc && (
            <p className="text-xs text-gray-400 -mt-1">Účet „admin“ nelze přejmenovat.</p>
          )}
          {usernameChanged && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 -mt-1">
              {form.email
                ? <>Po uložení se uživatel bude přihlašovat jako <strong>{form.username.trim()}</strong>. Na e-mail mu přijde oznámení s novým jménem.</>
                : <>Po uložení se uživatel bude přihlašovat jako <strong>{form.username.trim()}</strong>. Nemá e-mail — nové jméno mu musíte sdělit sami.</>}
            </p>
          )}
          {field('E-mail', 'email', 'email')}
          {field(
            isNew
              ? (form.email ? 'Heslo (prázdné = odkaz e-mailem)' : 'Heslo *')
              : 'Nové heslo (prázdné = beze změny)',
            'password', 'password',
            { required: passwordRequired, autoComplete: 'new-password' }
          )}
          {isNew && form.email && (
            <p className="text-xs text-gray-400 -mt-1">
              Necháte-li heslo prázdné, uživateli přijde e-mail s odkazem pro nastavení hesla.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            {field('Jméno', 'first_name')}
            {field('Příjmení', 'last_name')}
          </div>
          {field('Zobrazované jméno', 'display_name')}

          <div className="grid grid-cols-2 gap-3">
            {field('Platnost od', 'valid_from', 'date')}
            {field('Platnost do', 'valid_to', 'date')}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Poznámka administrátora</label>
            <textarea
              value={form.admin_note}
              onChange={e => setForm(f => ({ ...f, admin_note: e.target.value }))}
              rows={3}
              maxLength={2000}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <span className="text-sm text-gray-700">
              Administrátor
              {(isSelf || isAdminAcc) && <span className="text-xs text-gray-400"> — nelze změnit</span>}
            </span>
            <button
              type="button"
              disabled={isSelf || isAdminAcc}
              onClick={() => setForm(f => ({ ...f, is_admin: f.is_admin === 'Y' ? 'N' : 'Y' }))}
              className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-40 ${form.is_admin === 'Y' ? 'bg-blue-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_admin === 'Y' ? 'translate-x-5' : ''}`} />
            </button>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-3 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
              Zrušit
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {mutation.isPending ? 'Ukládám…' : (isNew ? 'Vytvořit' : 'Uložit')}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ─── Hlavní layout ─────────────────────────────────────────────────────────────

export function AdminUsersLayout() {
  const navigate   = useNavigate()
  // Sledování přístupů (etapa K2) — admin obrazovky jsou systémové části bez uzlu v registru.
  useTrackView('admin', 'admin-users')
  const { user }   = useAuthStore()
  const qc         = useQueryClient()
  const [modal,      setModal]      = useState(null)   // null | 'new' | { ...user }
  const [expandedId, setExpandedId] = useState(null)   // null | user.id
  const [dialog,     setDialog]     = useState(null)   // { type, user }
  const [linkResult, setLinkResult] = useState(null)   // { reset_link, expires_at, email_sent, message }

  if (user && user.is_admin !== 'Y') {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Přístup odepřen — potřebujete práva administrátora.
      </div>
    )
  }

  const { data: users = [], isLoading, isError } = useQuery({
    queryKey: ['admin-users'],
    queryFn:  adminApi.getUsers,
    enabled:  !!user,
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => adminApi.setUserStatus(id, { status }),
    onSuccess:  ()   => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  function toggleExpand(id) {
    setExpandedId(prev => prev === id ? null : id)
  }

  function afterMutation() {
    qc.invalidateQueries({ queryKey: ['admin-users'] })
    setDialog(null)
  }

  // Seřadit: pending nahoru (ať admin hned vidí, co vyřídit), pak podle username.
  const sorted = [...users].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1
    if (b.status === 'pending' && a.status !== 'pending') return 1
    return (a.username || '').localeCompare(b.username || '')
  })

  const HEAD = ['Uživatelské jméno', 'Jméno', 'E-mail', 'Stav', 'Admin', 'Práva', 'Platnost', 'Poslední přihl.', '']

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/')}
          className="text-gray-400 hover:text-gray-700 transition-colors text-lg">←</button>
        <h1 className="text-xl font-bold text-gray-800">Správa uživatelů</h1>
        <span className="ml-auto text-sm text-gray-400">{users.length} uživatelů</span>
        <button
          onClick={() => setModal('new')}
          className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
        >
          + Nový uživatel
        </button>
      </div>

      <div className="p-6">
        {isLoading && <p className="text-gray-400 text-sm">Načítám…</p>}
        {isError   && <p className="text-red-500 text-sm">Chyba při načítání uživatelů.</p>}

        {!isLoading && !isError && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-8" />
                  {HEAD.map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(u => {
                  const sm = STATUS_META[u.status] || { label: u.status, cls: 'bg-gray-100 text-gray-500' }
                  const isAdminAcc = u.username === 'admin'
                  const canDelete  = u.id !== user?.id && u.is_admin !== 'Y' && !isAdminAcc
                  const canBlock   = u.is_admin !== 'Y'
                  const validRange = [formatDateOnly(u.valid_from), formatDateOnly(u.valid_to)]
                  return (
                    <Fragment key={u.id}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="pl-3">
                          <button
                            onClick={() => toggleExpand(u.id)}
                            className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors text-xs"
                            title="Práva uživatele"
                          >
                            {expandedId === u.id ? '▼' : '▶'}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-800">
                          {u.username}
                          {u.admin_note && (
                            <span title={u.admin_note} className="ml-1 cursor-help">📝</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.display_name || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{u.email || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1 items-start">
                            <Badge className={sm.cls}>{sm.label}</Badge>
                            {u.must_change_password === 'Y' && (
                              <Badge className="bg-orange-100 text-orange-700">Čeká na heslo</Badge>
                            )}
                            {u.registration_source === 'self' && (
                              <Badge className="bg-purple-50 text-purple-600">Samoregistrace</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={u.is_admin === 'Y' ? 'bg-purple-100 text-purple-700' : 'text-gray-300'}>
                            {u.is_admin === 'Y' ? 'Admin' : '—'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <UserAccessBadges userId={u.id} />
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">
                          {validRange[0] || validRange[1]
                            ? `${validRange[0] || '…'} – ${validRange[1] || '…'}`
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-400">{formatDate(u.last_login)}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1.5 justify-end">
                            {u.status === 'pending' && (
                              <>
                                <button onClick={() => setDialog({ type: 'approve', user: u })}
                                  className="px-2.5 py-1 text-xs rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors">
                                  Schválit
                                </button>
                                <button onClick={() => setDialog({ type: 'reject', user: u })}
                                  className="px-2.5 py-1 text-xs rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                                  Zamítnout
                                </button>
                              </>
                            )}
                            {u.status === 'approved' && canBlock && (
                              <button onClick={() => statusMutation.mutate({ id: u.id, status: 'blocked' })}
                                className="px-2.5 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors">
                                Blokovat
                              </button>
                            )}
                            {u.status === 'blocked' && (
                              <button onClick={() => statusMutation.mutate({ id: u.id, status: 'approved' })}
                                className="px-2.5 py-1 text-xs rounded-lg border border-green-300 text-green-700 hover:bg-green-50 transition-colors">
                                Odblokovat
                              </button>
                            )}
                            {u.status === 'approved' && (
                              <button
                                onClick={() => setDialog({ type: 'reset', user: u })}
                                className="px-2.5 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                              >
                                Reset hesla
                              </button>
                            )}
                            <button onClick={() => setModal(u)}
                              className="px-2.5 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors">
                              Editovat
                            </button>
                            <button
                              onClick={() => setDialog({ type: 'delete', user: u })}
                              disabled={!canDelete}
                              title={!canDelete ? 'Administrátora / účet „admin" nelze smazat' : ''}
                              className="px-2.5 py-1 text-xs rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              Smazat
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandedId === u.id && <UserAccessRow userId={u.id} />}
                    </Fragment>
                  )
                })}
                {users.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">Žádní uživatelé</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal !== null && (
        <UserModal
          user={modal === 'new' ? null : modal}
          currentUserId={user?.id}
          onClose={() => setModal(null)}
          onResult={(res) => setLinkResult(res)}
        />
      )}

      {dialog?.type === 'approve' && (
        <ApproveDialog
          user={dialog.user}
          onClose={() => setDialog(null)}
          onApproved={(res) => {
            afterMutation()
            if (res?.reset_link) setLinkResult(res)
          }}
        />
      )}
      {dialog?.type === 'reject' && (
        <RejectDialog user={dialog.user} onClose={() => setDialog(null)} onDone={afterMutation} />
      )}
      {dialog?.type === 'delete' && (
        <DeleteUserDialog user={dialog.user} onClose={() => setDialog(null)} onDone={afterMutation} />
      )}
      {dialog?.type === 'reset' && (
        <ResetPasswordDialog
          user={dialog.user}
          onClose={() => setDialog(null)}
          onDone={(res) => { afterMutation(); if (res?.reset_link) setLinkResult(res) }}
        />
      )}

      {linkResult && (linkResult.reset_link
        ? <ResetLinkDialog result={linkResult} onClose={() => setLinkResult(null)} />
        : <MessageDialog title="Uživatelské jméno změněno" message={linkResult.message} onClose={() => setLinkResult(null)} />
      )}
    </div>
  )
}
